// @flow

import React, { Component } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CollapsibleWizard,
  Step,
} from "@components";
import Step1Rule from "./step1-rule";
import Step2Assignments from "./step2-assignments";
import type { State } from "@store/reducers";
import type { EmployeeSmsRule } from "@modules/service/reducer/employee-sms-notifications/types";
import type { HierarchyAssignment } from "@modules/service/types";
import { removeNullAndUndefinedProps } from "@transformers/object";

type OwnProps = $Exact<{
  defaultStep: 1 | 2,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  current: ?EmployeeSmsRule,
  assignments: Array<HierarchyAssignment>,
  hierarchy: ?HierarchyTree,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setCurrent: typeof actionCreators.SetEmployeeSmsCurrent,
  setAssignments: typeof actionCreators.SetEmployeeSmsAssignments,
  update: typeof actionCreators.UpdateEmployeeSmsRule,
  setShowModal: typeof actionCreators.SetShowEditEmployeeSmsModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class EmployeeSmsEditModal extends Component<Props> {
  validate: *;

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validate = new Ajv().compile(this.props.schema);
    }
  }

  get isValid() {
    return !this.validate || this.validate(removeNullAndUndefinedProps(this.props.current || {}));
  }

  handleCancel = () => {
    this.props.setShowModal(false);
  };

  handleSubmit = () => {
    this.props.update({
      clientId: this.props.clientId,
      rule: this.props.current,
      assignments: this.props.assignments,
    });
  };

  handleChange = ([step1, step2]: Array<Object> = []) => {
    this.props.setCurrent(step1);

    if (step2) {
      this.props.setAssignments(step2.assignments);
    }
  };

  get stepData() {
    return [
      {
        data: this.props.current,
        valid: true,
      },
      {
        data: {
          assignments: this.props.assignments,
        },
        valid: true,
      },
    ];
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EMPLOYEE_SMS_MODAL_EDIT_TITLE", {
            id: (this.props.current || {}).id,
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          {this.props.schema ? (
            <CollapsibleWizard
              defaultStep={this.props.defaultStep}
              data={this.stepData}
              onChange={this.handleChange}
            >
              <Step
                component={Step1Rule}
                title={this.props.t("EMPLOYEE_SMS_MODAL_STEP1_TITLE")}
                schema={this.props.schema}
                editing
              />
              <Step
                component={Step2Assignments}
                title={this.props.t("EMPLOYEE_SMS_MODAL_STEP2_TITLE")}
                hierarchy={this.props.hierarchy}
                assignments={this.props.assignments}
                editing
              />
            </CollapsibleWizard>
          ) : null}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            onClick={this.handleSubmit}
            disabled={!(this.isValid && this.props.isAssignmentsValid)}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    schema: serviceSelectors.getEmployeeSmsSchema(state),
    current: serviceSelectors.getEmployeeSmsCurrent(state),
    assignments: serviceSelectors.getEmployeeSmsAssignments(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    isAssignmentsValid: serviceSelectors.isEmployeeSmsAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setCurrent: actionCreators.SetEmployeeSmsCurrent,
  setAssignments: actionCreators.SetEmployeeSmsAssignments,
  update: actionCreators.UpdateEmployeeSmsRule,
  setShowModal: actionCreators.SetShowEditEmployeeSmsModal,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmployeeSmsEditModal);
